import { cloneDeep, get, set } from 'lodash';

import type { ArrayElement, DataSource } from './types';

// ---------------------------------------------------------------------------
// Request — root of the chain, backed by React Query + HTTP mutations
// ---------------------------------------------------------------------------

type RequestMode = 'put' | 'post';

interface RequestState<T> {
  queryData: T | undefined;
  loading: boolean;
  mode: RequestMode;
  id: string | undefined;
  create: (payload: any) => Promise<any>;
  update: (id: string, payload: any) => Promise<any>;
  onSaveSuccess: () => void;
}

export class Request<T> implements DataSource<T> {
  private _state: RequestState<T> = {
    queryData: undefined,
    loading: false,
    mode: 'post',
    id: undefined,
    create: () => Promise.resolve(),
    update: () => Promise.resolve(),
    onSaveSuccess: () => {},
  };

  get value(): T | undefined {
    return this._state.queryData;
  }

  get loading(): boolean {
    return this._state.loading;
  }

  _syncState(state: RequestState<T>) {
    this._state = state;
  }

  async save(data: unknown, _path: PropertyKey[] = []): Promise<void> {
    if (this._state.mode === 'post') {
      await this._state.create(data);
    } else {
      await this._state.update(this._state.id!, data);
    }
    this._state.onSaveSuccess();
  }

  get<K extends keyof T & string>(key: K): DataSource<T[K]> {
    return new Accessor<T, K>(this, key);
  }

  findBy(predicate: (item: ArrayElement<T>) => boolean): DataSource<ArrayElement<T>> {
    return new FindAccessor(this as DataSource<ArrayElement<T>[]>, predicate);
  }
}

// ---------------------------------------------------------------------------
// Accessor — keyed property access on a parent data source
// ---------------------------------------------------------------------------

export class Accessor<TParent, K extends keyof TParent & string> implements DataSource<TParent[K]> {
  constructor(
    private parent: DataSource<TParent>,
    private key: K
  ) {}

  get value(): TParent[K] | undefined {
    return this.parent.value?.[this.key];
  }

  get loading(): boolean {
    return this.parent.loading;
  }

  async save(data: unknown, path: PropertyKey[] = []): Promise<void> {
    return this.parent.save(data, [this.key, ...path]);
  }

  get<K2 extends keyof TParent[K] & string>(key: K2): DataSource<TParent[K][K2]> {
    return new Accessor<TParent[K], K2>(this, key);
  }

  findBy(predicate: (item: ArrayElement<TParent[K]>) => boolean): DataSource<ArrayElement<TParent[K]>> {
    return new FindAccessor(this as DataSource<ArrayElement<TParent[K]>[]>, predicate);
  }
}

// ---------------------------------------------------------------------------
// FindAccessor — predicate-based array element access
// ---------------------------------------------------------------------------

export class FindAccessor<TElement> implements DataSource<TElement> {
  constructor(
    private parent: DataSource<TElement[]>,
    private predicate: (item: TElement) => boolean
  ) {}

  get value(): TElement | undefined {
    return this.parent.value?.find(this.predicate);
  }

  get loading(): boolean {
    return this.parent.loading;
  }

  async save(data: unknown, path: PropertyKey[] = []): Promise<void> {
    const arr = this.parent.value;
    if (!arr) throw new Error('FindAccessor.save: parent array is undefined');
    const index = arr.findIndex(this.predicate);
    if (index === -1) throw new Error('FindAccessor.save: predicate matched no element');
    return this.parent.save(data, [index, ...path]);
  }

  get<K extends keyof TElement & string>(key: K): DataSource<TElement[K]> {
    return new Accessor<TElement, K>(this, key);
  }

  findBy(predicate: (item: ArrayElement<TElement>) => boolean): DataSource<ArrayElement<TElement>> {
    return new FindAccessor(this as DataSource<ArrayElement<TElement>[]>, predicate);
  }
}

// ---------------------------------------------------------------------------
// FormControlBuffer — a DataSource backed by form state instead of a Request.
// Allows child components to chain `.get()` / `.findBy()` off form values and
// call `.save()` to update the parent form state at the accumulated path.
// ---------------------------------------------------------------------------

export class FormControlBuffer<T extends object> implements DataSource<T> {
  private _formValues: T | undefined;
  private _loading = false;
  private _onUpdate: (updatedValues: T) => void = () => {};

  get value(): T | undefined {
    return this._formValues;
  }

  get loading(): boolean {
    return this._loading;
  }

  _syncState(state: { formValues: T; loading: boolean; onUpdate: (updatedValues: T) => void }) {
    this._formValues = state.formValues;
    this._loading = state.loading;
    this._onUpdate = state.onUpdate;
  }

  async save(data: unknown, path: PropertyKey[] = []): Promise<void> {
    if (!this._formValues) throw new Error('FormControlBuffer.save: form values are undefined');

    if (path.length === 0) {
      // Full form submit — data is the serialized payload, delegate to parent Request.
      // This shouldn't happen in normal flow; the form's submit() calls datasource.save().
      throw new Error('FormControlBuffer.save: full submit should go through the parent datasource');
    }

    // Partial update — set the value at the given path in the form state
    const updated = cloneDeep(this._formValues);
    set(updated, path, data);
    this._onUpdate(updated);
  }

  get<K extends keyof T & string>(key: K): DataSource<T[K]> {
    return new Accessor<T, K>(this, key);
  }

  findBy(predicate: (item: ArrayElement<T>) => boolean): DataSource<ArrayElement<T>> {
    return new FindAccessor(this as DataSource<ArrayElement<T>[]>, predicate);
  }

  /** Read a nested value by dot path (convenience for dynamic key access). */
  getByPath(path: string): unknown {
    return get(this._formValues, path);
  }
}
